const categoryContext = require.context(
  "../../../content/categories",
  false,
  /\.content\.json$/
);

const CATEGORY_KEY_ALIASES = {
  "arborist-tree-services": "landscaping",
  concrete: "concrete-foundation",
  "exterior-door-replacement": "windows-replacement",
  "fence-install": "fence-gates",
  "foundation-repair": "concrete-foundation",
  gates: "fence-gates",
  "pool-maintenance": "pools",
};

function normalizeCategoryKey(category) {
  if (!category) {
    return null;
  }

  const rawKey = String(category).trim();
  if (!rawKey) {
    return null;
  }

  const normalized = rawKey
    .replace(/\.html$/i, "")
    .replace(/\s+/g, "-")
    .replace(/_+/g, "-")
    .toLowerCase();

  return CATEGORY_KEY_ALIASES[normalized] || normalized;
}

function titleCase(value) {
  return String(value || "")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function humanizeSlug(slug) {
  return titleCase(String(slug || "").replace(/-/g, " "));
}

function normalizeOptions(options = []) {
  return options
    .map((option) => {
      if (typeof option === "string") {
        const trimmed = option.trim();
        return trimmed
          ? { value: trimmed.toLowerCase().replace(/\s+/g, "-"), label: trimmed }
          : null;
      }

      if (!option || typeof option !== "object") {
        return null;
      }

      const value = String(option.value || "").trim();
      const label = String(option.label || option.value || "").trim();

      if (!value || !label) {
        return null;
      }

      const normalizedOption = {
        value,
        label,
      };

      if (option.description) {
        normalizedOption.description = String(option.description).trim();
      }

      return normalizedOption;
    })
    .filter(Boolean);
}

function normalizeQuestion(question, index) {
  if (!question || typeof question !== "object") {
    return null;
  }

  const id = String(question.id || "").trim() || `question_${index + 1}`;
  const label = String(question.label || "").trim();

  if (!label) {
    return null;
  }

  const normalized = {
    id,
    label,
    inputType: String(question.inputType || "singleChoice").trim() || "singleChoice",
    dimension: String(question.dimension || "").trim() || null,
    options: normalizeOptions(question.options),
  };

  if (question.helperText) {
    normalized.helperText = String(question.helperText).trim();
  }

  return normalized;
}

function normalizeQuestionSet(questions = []) {
  return questions
    .map((question, index) => normalizeQuestion(question, index))
    .filter(Boolean)
    .slice(0, 4);
}

const FALLBACK_PROJECT_COST_ESTIMATOR = {
  heading: "Project Cost Estimator",
  description:
    "Answer four quick questions so we can estimate the likely scope, urgency, and cost range for your project.",
  questions: normalizeQuestionSet([
    {
      id: "scope",
      label: "How much of the project do you expect to change?",
      helperText: "Scope is the most important driver of a useful cost range.",
      inputType: "singleChoice",
      dimension: "scope",
      options: [
        { value: "small", label: "A small repair or light refresh" },
        { value: "medium", label: "Several components or a moderate upgrade" },
        { value: "large", label: "A major remodel, replacement, or full project" },
      ],
    },
    {
      id: "severity",
      label: "What is the main reason you are considering the project?",
      helperText: "Severity helps us describe whether this is mostly cosmetic or more involved.",
      inputType: "singleChoice",
      dimension: "severity",
      options: [
        { value: "cosmetic", label: "Mostly an update or visual improvement" },
        { value: "functional", label: "It is worn out, inefficient, or hard to use" },
        { value: "serious", label: "There is damage, a safety concern, or a recurring problem" },
      ],
    },
    {
      id: "extent",
      label: "How much area or how many components are likely involved?",
      helperText: "Extent helps estimate the amount of labor and materials involved.",
      inputType: "singleChoice",
      dimension: "extent",
      options: [
        { value: "small", label: "One area or a limited amount of work" },
        { value: "medium", label: "A room, section, or several connected items" },
        { value: "large", label: "Multiple areas, hidden conditions, or major site work" },
      ],
    },
    {
      id: "urgency",
      label: "How soon are you hoping to move forward?",
      helperText: "Urgency mainly helps the AI describe timing and next steps.",
      inputType: "singleChoice",
      dimension: "urgency",
      options: [
        { value: "planning", label: "I am just planning ahead" },
        { value: "soon", label: "I want to start within the next few weeks or months" },
        { value: "urgent", label: "I need help as soon as possible" },
      ],
    },
  ]),
};

function findCategoryContent(categoryKey) {
  const normalizedKey = normalizeCategoryKey(categoryKey);
  if (!normalizedKey) {
    return null;
  }

  const keys = categoryContext.keys();

  for (const key of keys) {
    const content = categoryContext(key);
    const fileSlug = key.replace("./", "").replace(".content.json", "");

    if (fileSlug === normalizedKey || String(content.slug || "").trim() === normalizedKey) {
      return content;
    }
  }

  return null;
}

export function getProjectCostEstimatorContent(categoryKey) {
  const normalizedKey = normalizeCategoryKey(categoryKey);
  const categoryContent = findCategoryContent(normalizedKey) || {};
  const fallback = FALLBACK_PROJECT_COST_ESTIMATOR;
  const block = categoryContent.projectCostEstimator || {};
  const questions = normalizeQuestionSet(block.questions || []);
  const safeQuestions = questions.length === 4 ? questions : fallback.questions;
  const breadcrumbs = Array.isArray(categoryContent.breadcrumbs)
    ? categoryContent.breadcrumbs
    : [];
  const categoryLabel =
    String(breadcrumbs[breadcrumbs.length - 1]?.label || "").trim() ||
    String(categoryContent.label || "").trim() ||
    humanizeSlug(normalizedKey);

  return {
    requestedKey: categoryKey || null,
    normalizedKey,
    categoryKey: normalizedKey || categoryKey || "general",
    categoryLabel: categoryLabel || "Home Project",
    heading: String(block.heading || fallback.heading).trim(),
    description: String(block.description || fallback.description).trim(),
    questions: safeQuestions,
    hasCustomQuestions: questions.length === 4,
  };
}

export { FALLBACK_PROJECT_COST_ESTIMATOR, normalizeCategoryKey };
